import { ChevronRight, Terminal } from 'lucide-react';
import { useId, useState } from 'react';

import { cn } from '@/lib/classnames';
import { previewDllCommandLine, previewExeCommandLine } from '@/lib/launchArgs';

/** How one entry should be launched, in the shape the submit body takes. */
export type LaunchOption = { exe?: { args: string }; dll?: { export?: string; args?: string } };

/** An entry a launch option can apply to. */
export type LaunchTarget = {
  id: string;
  /** The name the analyst recognises, which is the archive's own. */
  name: string;
  /** 'program' takes a command line; 'library' takes an export. */
  kind: 'program' | 'library';
};

/**
 * Picking the entries whose launch is configurable.
 *
 * `pe_kind` comes from the file's own COFF header, not its name, so a library
 * called `.exe` is offered the export field — which is the only one that could
 * run it.
 */
export function launchTargets(
  entries: { id: string; name: string; pe_kind?: string; selectable?: boolean }[],
  selected: Set<string> | null
): LaunchTarget[] {
  return entries
    .filter((e) => e.selectable !== false)
    .filter((e) => selected === null || selected.has(e.id))
    .flatMap((e) =>
      e.pe_kind === 'program' || e.pe_kind === 'library'
        ? [{ id: e.id, name: e.name, kind: e.pe_kind }]
        : []
    );
}

/** What the collapsed row says it is holding, so its state needs no expanding. */
function summarise(targets: LaunchTarget[], options: Record<string, LaunchOption>): string {
  const set = targets
    .map((t) => options[t.id])
    .filter((o): o is LaunchOption => o !== undefined)
    .map((o) => o.exe?.args?.trim() || o.dll?.export?.trim() || o.dll?.args?.trim() || '')
    .filter((s) => s !== '');
  if (set.length === 0) return 'default';
  if (set.length === 1) return set[0];
  return `${set.length} files configured`;
}

/**
 * How the guest should start the files being submitted.
 *
 * A sibling of OptionsPanel rather than a section inside it, because this is
 * the one setting that is PER ENTRY: the rest describe the machine a run lands
 * on, which is the same question whatever the file is, and OptionsPanel's own
 * comment promises they apply to everything in the tree. A command line
 * describes the file.
 *
 * Three decisions, each the answer to a mistake the obvious design makes:
 *
 * - It does not render at all unless a picked entry can take one. Someone
 *   submitting a document never sees a disabled control they have to reason
 *   about.
 * - Collapsed, but never blank: the row says `default`, or shows the value, so
 *   the state is legible without opening it. A collapsed control that hides
 *   whether it is set is how a setting gets forgotten.
 * - One file reads as one form. With a single entry there is no per-entry
 *   heading, because that is the overwhelming common case; with several, each
 *   gets its name and the badge the file tree already labels it with.
 */
export function LaunchOptionsPanel({
  targets,
  options,
  onChange,
  disabled,
}: {
  targets: LaunchTarget[];
  options: Record<string, LaunchOption>;
  onChange: (next: Record<string, LaunchOption>) => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  if (targets.length === 0) return null;

  function set(id: string, option: LaunchOption) {
    const next = { ...options };
    // An entry configured back to nothing is removed rather than left as an
    // empty object: the server reads an empty arm as the default anyway, and
    // keeping it would make the summary claim a file is configured.
    const empty =
      !option.exe?.args?.trim() && !option.dll?.export?.trim() && !option.dll?.args?.trim();
    if (empty) delete next[id];
    else next[id] = option;
    onChange(next);
  }

  return (
    <div className="card divide-y divide-rule">
      <section className="px-4 py-4">
        <button
          type="button"
          className="btn-ghost btn-sm"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((v) => !v)}
        >
          <ChevronRight
            aria-hidden
            className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')}
          />
          <Terminal size={16} className="text-ink-500" aria-hidden />
          <span className="text-prose font-medium text-ink-100">Launch options</span>
          <span className="text-mini text-ink-500">{summarise(targets, options)}</span>
        </button>

        {open && (
          <div id={panelId} className="mt-3 space-y-5">
            {targets.map((target) => (
              <TargetFields
                key={target.id}
                target={target}
                showName={targets.length > 1}
                option={options[target.id] ?? {}}
                onChange={(o) => set(target.id, o)}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function TargetFields({
  target,
  showName,
  option,
  onChange,
  disabled,
}: {
  target: LaunchTarget;
  showName: boolean;
  option: LaunchOption;
  onChange: (next: LaunchOption) => void;
  disabled: boolean;
}) {
  const argsId = useId();
  const exportId = useId();
  const isProgram = target.kind === 'program';
  const args = (isProgram ? option.exe?.args : option.dll?.args) ?? '';
  const exportName = option.dll?.export ?? '';

  function emit(nextArgs: string, nextExport: string) {
    onChange(
      isProgram ? { exe: { args: nextArgs } } : { dll: { export: nextExport, args: nextArgs } }
    );
  }

  return (
    <div className="space-y-2.5">
      {showName && (
        <div className="flex items-center gap-2">
          <span className="text-prose text-ink-100">{target.name}</span>
          <span className="text-mini text-ink-500">
            PE {isProgram ? 'program' : 'library'}
          </span>
        </div>
      )}

      {!isProgram && (
        <label className="block" htmlFor={exportId}>
          <span className="text-mini text-ink-400">Export</span>
          <input
            id={exportId}
            className="input mt-1"
            type="text"
            placeholder="DllRegisterServer"
            value={exportName}
            disabled={disabled}
            onChange={(e) => emit(args, e.target.value)}
          />
          <span className="mt-1 block text-mini text-ink-500">
            A name, or <code>#7</code> for an ordinal. Leave empty and the sandbox reads the
            DLL&apos;s own exports, preferring <code>DllRegisterServer</code>.
          </span>
        </label>
      )}

      <label className="block" htmlFor={argsId}>
        <span className="text-mini text-ink-400">Arguments</span>
        <input
          id={argsId}
          className="input mt-1"
          type="text"
          placeholder={isProgram ? '--install /silent' : '-i "C:\\payload"'}
          value={args}
          disabled={disabled}
          onChange={(e) => emit(e.target.value, exportName)}
        />
        <span className="mt-1 block text-mini text-ink-500">
          {isProgram
            ? 'Quote an argument containing a space. Leave empty to start it with none.'
            : 'Passed to the export as a single string.'}
        </span>
      </label>

      {/* What will actually run. Windows quoting is the one thing an analyst
          cannot predict, and showing the result here is cheaper than spending a
          run to find out. */}
      <p className="text-mini text-ink-500">
        Runs as{' '}
        <code className="text-ink-300">
          {isProgram
            ? previewExeCommandLine(target.name, args)
            : previewDllCommandLine(target.name, exportName, args)}
        </code>
      </p>
    </div>
  );
}
